import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram, MessageCircle } from "lucide-react";
import { LogoMark, Wordmark } from "@/components/Logo";
import { CONTACTO, NAV_LINKS, SOCIAL } from "@/constants/site";
import { scrollToSection } from "@/lib/scroll";

const LEGALES = [
  { label: "Aviso legal", href: "/aviso-legal" },
  { label: "Política de privacidad", href: "/politica-de-privacidad" },
  { label: "Política de cookies", href: "/politica-de-cookies" },
];

const REDES = [
  { nombre: "Instagram", icon: Instagram, url: SOCIAL.instagram },
  { nombre: "WhatsApp", icon: MessageCircle, url: SOCIAL.whatsapp },
];

export const Footer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (href) => {
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(href), 400);
    } else {
      scrollToSection(href);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-chocolate text-crema">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <LogoMark className="h-10 w-10" />
              <Wordmark light />
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-crema/65">
              Panadería y pastelería artesanal. El sabor de lo artesanal, hecho cada día.
            </p>
            <div className="mt-6 flex gap-3">
              {REDES.map(({ nombre, icon: Icon, url }) =>
                url ? (
                  <a
                    key={nombre}
                    data-testid={`footer-social-${nombre.toLowerCase()}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={nombre}
                    className="grid h-10 w-10 place-items-center rounded-full border border-crema/25 transition-colors hover:bg-crema/10"
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.8} />
                  </a>
                ) : (
                  <span
                    key={nombre}
                    data-testid={`footer-social-${nombre.toLowerCase()}-pending`}
                    title="Próximamente"
                    aria-label={`${nombre} (próximamente)`}
                    className="grid h-10 w-10 cursor-not-allowed place-items-center rounded-full border border-crema/15 text-crema/35"
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.8} />
                  </span>
                )
              )}
            </div>
          </div>

          <nav aria-label="Enlaces rápidos">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-crema/50">
              Enlaces rápidos
            </h3>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    data-testid={`footer-link-${l.href.slice(1)}`}
                    onClick={() => go(l.href)}
                    className="text-sm text-crema/75 transition-colors hover:text-crema"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Información legal">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-crema/50">
              Legal
            </h3>
            <ul className="mt-5 space-y-3">
              {LEGALES.map((l) => (
                <li key={l.href}>
                  <Link
                    data-testid={`footer-legal-${l.href.slice(1)}`}
                    to={l.href}
                    className="text-sm text-crema/75 transition-colors hover:text-crema"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-crema/50">
              Contacto
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-crema/75">
              <li>
                <a
                  data-testid="footer-phone"
                  href={CONTACTO.telefonoHref}
                  className="transition-colors hover:text-crema"
                >
                  {CONTACTO.telefono}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-email"
                  href={CONTACTO.emailHref}
                  className="break-all transition-colors hover:text-crema"
                >
                  {CONTACTO.email}
                </a>
              </li>
              <li>
                {CONTACTO.direccion1}, {CONTACTO.direccion2}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-crema/15 pt-8 sm:flex-row">
          <p className="text-xs text-crema/55" data-testid="footer-copyright">
            © {new Date().getFullYear()} Pastelería Panadería Rawan. Todos los derechos reservados.
          </p>
          <p className="text-xs text-crema/40">100 % artesanal · elaborado cada día</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
